import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar, LoadingSpinner, ErrorState } from '../components';
import { eventService, friendService, gatheringService, partyEngineService, plannerService } from '../services';
import { useUser, useAuth } from '../context';
import ringArt from '../assets/img/portal-ring.png';
import { tintFor } from '../utils/tints';
import { dateLabel } from '../utils/surfaceFormat';
import { realPlace } from '../utils/placeLabel';
import { storedLocation } from '../data/routeData';
import { primeHostSummary } from '../hooks/useHostSummary';
import HostEventCard from '../components/host/dashboard/HostEventCard';
import BringPeopleIn from '../components/host/dashboard/BringPeopleIn';
import { usePeopleToast } from '../components/people/PeopleUI';
import { FILTERS, bucketOf, emptyLabel, planSegments } from '../components/host/dashboard/hostDashboardModel';
import '../components/host/dashboard/hostDashboard.css';

/**
 * /host — round-3 handoff §2 (docs/plans/2026-09-header-host-pros/handoff/README.md).
 *
 * FIND YOUR PEOPLE. over the create actions; the claim banner when the Party Engine has
 * parties looking for a host; YOUR EVENTS as cards under DRAFT · LIVE · ENDED (the filter
 * mirrored to the URL hash); MY PLANS and GATHERINGS in the rail; BRING PEOPLE IN at the
 * foot. Every read is the same call the old table made.
 */
const PlusGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M5 12h14M12 5v14" /></svg>
);
const PlanGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M4 5h16M4 12h10M4 19h7" /><path d="M16 17l2 2 4-4" /></svg>
);

const ACTIONS = [
  { key: 'party', label: '+ CREATE EVENT', hint: 'Tickets or an event fund', path: '/host/events/create', primary: true },
  { key: 'plan', label: 'PLAN AN EVENT', hint: 'Venue, sound, staff, money', path: '/host/events/plan', plan: true },
  { key: 'gathering', label: '+ GATHERING', hint: 'A weekend, a series', path: '/gatherings/new' },
  { key: 'club', label: '+ CLUB', hint: 'Turn people into members', path: '/clubs/create' },
];

const cityOf = (label) => (label ? label.split(',')[0].trim() : '');

export default function HostDashboardPage() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { profile, walletBalance, canUsePlanner } = useUser();
  // No swap here: this grid already offers + CLUB, so a second START A CLUB would be two
  // buttons for one thing. The planner card simply isn't dealt when it isn't theirs.
  const actions = ACTIONS.filter((a) => a.key !== 'plan' || canUsePlanner);
  const { logout } = useAuth();
  const [notify, toast] = usePeopleToast();

  const [events, setEvents] = useState([]);
  const [gatherings, setGatherings] = useState([]);
  // Party Engine: parties seeded by the platform still looking for a host.
  const [claimable, setClaimable] = useState([]);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(true);
  const plansRef = useRef(null);
  const gatheringsRef = useRef(null);

  // The filter lives in the hash (#draft · #live · #ended) so a link lands on the right list.
  const hashFilter = (hash || '').replace('#', '');
  const filter = FILTERS.some(([k]) => k === hashFilter) ? hashFilter : 'live';
  const setFilter = (k) => navigate(`#${k}`, { replace: true });

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await eventService.getEvents();
      if (result.success) {
        const list = result.data || [];
        setEvents(list);
        // The bar's HOST bubble can read this list instead of asking again.
        primeHostSummary({ liveEventCount: list.filter((e) => bucketOf(e.status) === 'live').length });
      } else {
        setError(result.error || 'Failed to load events');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    let cancelled = false;
    // Without the planner this route now 403s; don't ask for plans that cannot exist.
    if (canUsePlanner) plannerService.mine().then((r) => { if (!cancelled && r.success) setPlans((r.data || []).filter((p) => p.status !== 'archived')); });
    partyEngineService.getClaimable().then((r) => { if (!cancelled && r.success) setClaimable(r.data || []); });
    gatheringService.listMine().then((r) => { if (!cancelled && r.success) setGatherings(r.data || []); });
    friendService.getSuggestedFriends(12).then((r) => {
      if (cancelled) return;
      if (r.success) setSuggested(r.data || []);
      setSuggestedLoading(false);
    });
    return () => { cancelled = true; };
  }, [canUsePlanner]);

  const counts = useMemo(() => {
    const c = { draft: 0, live: 0, ended: 0 };
    events.forEach((e) => { const b = bucketOf(e.status); if (b) c[b] += 1; });
    return c;
  }, [events]);
  const shown = useMemo(() => events.filter((e) => bucketOf(e.status) === filter), [events, filter]);

  const city = cityOf(realPlace(storedLocation().label, profile?.location));
  const claimCity = cityOf(claimable[0]?.regionName) || city || 'your area';

  const handleLogout = () => { logout(); navigate('/'); };
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="hd-page">
      <Navbar user={profile} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/')} />

      <section className="hd-intro">
        <div className="hd-container hd-intro-inner">
          <div className="hd-intro-top">
            <div style={{ minWidth: 0 }}>
              <div className="hd-kicker">HOST{city ? ` · ${city.toUpperCase()}` : ''}</div>
              <h1 className="hd-title">FIND YOUR PEOPLE.</h1>
              <p className="hd-sub">Gather them.</p>
            </div>
            <div className="hd-stats">
              <button type="button" className="hd-stat" data-live="" onClick={() => setFilter('live')} aria-label={`${counts.live} live — show live events`}>
                <i aria-hidden /><b>{counts.live}</b><span>LIVE</span>
              </button>
              <button type="button" className="hd-stat" onClick={() => scrollTo(plansRef)}><b>{plans.length}</b><span>PLANS</span></button>
              <button type="button" className="hd-stat" onClick={() => scrollTo(gatheringsRef)}><b>{gatherings.length}</b><span>GATHERINGS</span></button>
            </div>
          </div>
          <div className="hd-actions">
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                className="hd-action"
                data-primary={a.primary ? '' : undefined}
                data-plan={a.plan ? '' : undefined}
                onClick={() => navigate(a.path)}
              >
                <span className="hd-action-icon">
                  {a.primary && <i style={{ backgroundImage: `url(${ringArt})` }} aria-hidden />}
                  <span>{a.plan ? <PlanGlyph /> : <PlusGlyph />}</span>
                </span>
                <span className="hd-action-text"><b>{a.label}</b><span>{a.hint}</span></span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="hd-container hd-main">
        {claimable.length > 0 && (
          <button type="button" className="hd-claim" onClick={() => navigate('/host/claim')} data-testid="claim-banner">
            <span className="hd-claim-shine" aria-hidden />
            <span className="hd-claim-left">
              <span className="hd-claim-n">{claimable.length}<i aria-hidden /></span>
              <span className="hd-claim-text">
                <b>LOOKING FOR A HOST</b>
                <span>{claimable.length === 1 ? 'An event' : `${claimable.length} events`} in {claimCity} need{claimable.length === 1 ? 's' : ''} someone to run {claimable.length === 1 ? 'it' : 'them'}.</span>
              </span>
            </span>
            <span className="hd-claim-cta">CLAIM ONE <i>→</i></span>
          </button>
        )}

        <div className="hd-grid">
          <div className="hd-list">
            <div className="hd-list-head">
              <h2 className="hd-h2">YOUR EVENTS</h2>
              <div className="hd-filters" role="tablist" aria-label="Event filter">
                {FILTERS.map(([k, label]) => (
                  <button key={k} type="button" role="tab" className="hd-filter" data-on={filter === k ? '' : undefined} aria-selected={filter === k} onClick={() => setFilter(k)}>
                    {label}<span>{counts[k]}</span>
                  </button>
                ))}
              </div>
              <span className="hd-rule" />
            </div>

            {isLoading ? (
              <div className="hd-empty"><LoadingSpinner label="Loading events..." /></div>
            ) : error ? (
              <div className="hd-empty"><ErrorState message={error} onRetry={fetchEvents} /></div>
            ) : shown.length === 0 ? (
              <div className="hd-empty">
                <span>{emptyLabel(filter)}</span>
                <button type="button" className="hd-manage" data-dark="" onClick={() => navigate('/host/events/create')}>+ CREATE EVENT</button>
              </div>
            ) : (
              shown.map((e) => <HostEventCard key={`${filter}:${e.id}`} event={e} />)
            )}
          </div>

          <aside className="hd-side">
            <div className="hd-panel" ref={plansRef}>
              <div className="hd-panel-head">
                <span>MY PLANS</span>
                <button type="button" onClick={() => navigate('/host/events/plan')}>NEW PLAN →</button>
              </div>
              {plans.length === 0 && <span className="hd-panel-empty">NO PLANS YET.</span>}
              {plans.slice(0, 3).map((p) => {
                const approved = p.status === 'approved' || p.status === 'booked';
                const meta = [cityOf(p.regionName)?.toUpperCase(), dateLabel(p.updatedAt)].filter(Boolean).join(' · ');
                return (
                  <button key={p.id} type="button" className="hd-plan" onClick={() => navigate(`/host/events/plan/${p.id}`)}>
                    <span className="hd-plan-top">
                      <span>{p.title || p.prompt}</span>
                      <b data-approved={approved ? '' : undefined}>{(p.status || '').replace('_', ' ').toUpperCase()}</b>
                    </span>
                    <span className="hd-segments" aria-label={`${p.stagesDone || 0} of ${p.stageCount || 7} stages done`}>
                      {planSegments(p).map((done, i) => <i key={i} data-done={done ? '' : undefined} />)}
                    </span>
                    <span className="hd-plan-meta">{meta}</span>
                  </button>
                );
              })}
            </div>

            <div className="hd-panel" ref={gatheringsRef}>
              <div className="hd-panel-head">
                <span>GATHERINGS</span>
                <button type="button" onClick={() => navigate('/gatherings/new')}>+ NEW →</button>
              </div>
              {gatherings.length === 0 && <span className="hd-panel-empty">NO GATHERINGS YET.</span>}
              {gatherings.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="hd-gathering"
                  onClick={() => navigate(g.clubId ? `/clubs/${g.clubId}/gatherings/${g.id}` : `/gatherings/${g.id}`)}
                >
                  <span className="hd-gathering-n" style={{ background: g.eventCount ? tintFor(g.id) : '#f2f2f2' }}>{g.eventCount || 0}</span>
                  <span className="hd-gathering-text">
                    <b>{(g.name || '').toUpperCase()}</b>
                    <span className="hd-gathering-meta">{g.eventCount || 0} {g.eventCount === 1 ? 'EVENT' : 'EVENTS'} · {g.clubId ? (g.clubName || 'CLUB').toUpperCase() : 'STANDALONE'}</span>
                  </span>
                  {g.status !== 'published' && <span className="hd-chip">{(g.status || 'DRAFT').toUpperCase()}</span>}
                  <span aria-hidden style={{ flexShrink: 0, fontSize: 14 }}>→</span>
                </button>
              ))}
            </div>
          </aside>
        </div>

        <BringPeopleIn
          suggestions={suggested}
          loading={suggestedLoading}
          onSent={(person, r) => notify(person ? `REQUEST SENT TO ${(person.displayName || '').toUpperCase()}` : (r?.error || 'Could not send that request.'))}
        />
      </main>
      {toast}
    </div>
  );
}
